
import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils/cn";
import {
  IconPlay, IconPause, IconVolume, IconVolumeOff,
  IconMaximize, IconMinimize, IconDownload, IconSkipBack, IconSkipForward
} from "@/components/ui/Icons";

function formatTime(s: number) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

interface VideoPlayerProps {
  src: string;
  poster?: string;
  title?: string;
  downloadable?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  className?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

export function VideoPlayer({
  src, poster, title, downloadable = true,
  autoplay = false, loop = false, className,
  onPlay, onPause, onEnded,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout>>();

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(autoplay);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay_ = () => { setIsPlaying(true); onPlay?.(); };
    const onPause_ = () => { setIsPlaying(false); onPause?.(); };
    const onEnded_ = () => { setIsPlaying(false); onEnded?.(); };
    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onLoadedMetadata = () => { setDuration(video.duration); setIsLoading(false); };
    const onProgress = () => {
      if (video.buffered.length > 0) setBuffered(video.buffered.end(video.buffered.length - 1));
    };
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);

    video.addEventListener("play", onPlay_);
    video.addEventListener("pause", onPause_);
    video.addEventListener("ended", onEnded_);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("progress", onProgress);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("canplay", onCanPlay);
    document.addEventListener("fullscreenchange", onFullscreenChange);

    return () => {
      video.removeEventListener("play", onPlay_);
      video.removeEventListener("pause", onPause_);
      video.removeEventListener("ended", onEnded_);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("progress", onProgress);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("canplay", onCanPlay);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, [onPlay, onPause, onEnded]);

  const showControlsTemp = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimeout.current);
    hideTimeout.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 3000);
  }, []);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.paused ? v.play().catch(() => {}) : v.pause();
    showControlsTemp();
  }, [showControlsTemp]);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const bar = progressRef.current;
    const v = videoRef.current;
    if (!bar || !v || !duration) return;
    const rect = bar.getBoundingClientRect();
    v.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  }, [duration]);

  const skip = useCallback((secs: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.currentTime + secs, duration));
    showControlsTemp();
  }, [duration, showControlsTemp]);

  const toggleFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      await el.requestFullscreen().catch(() => {});
    } else {
      await document.exitFullscreen().catch(() => {});
    }
  }, []);

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = title ? `${title}.mp4` : "video.mp4";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // fallback
      const a = document.createElement("a");
      a.href = src;
      a.download = title ? `${title}.mp4` : "video.mp4";
      a.target = "_blank";
      a.click();
    } finally {
      setDownloading(false);
    }
  }, [src, title, downloading]);

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration ? (buffered / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className={cn("relative bg-black rounded-2xl overflow-hidden group select-none", className)}
      onMouseMove={showControlsTemp}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onTouchStart={showControlsTemp}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        loop={loop}
        muted={isMuted}
        playsInline
        preload="metadata"
        className="w-full h-full object-contain"
        onClick={togglePlay}
      />

      {/* Loading spinner */}
      <AnimatePresence>
        {isLoading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Big play button (when paused) */}
      <AnimatePresence>
        {!isPlaying && !isLoading && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="w-16 h-16 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20 hover:bg-black/70 transition-colors">
              <IconPlay className="w-7 h-7 text-white ml-1" />
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Controls overlay */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-12 pb-3 px-3"
          >
            {/* Title */}
            {title && (
              <p className="text-white text-sm font-medium mb-2 truncate drop-shadow">{title}</p>
            )}

            {/* Progress bar */}
            <div
              ref={progressRef}
              onClick={handleProgressClick}
              className="relative h-1.5 bg-white/25 rounded-full mb-3 cursor-pointer group/bar"
            >
              {/* Buffered */}
              <div className="absolute inset-y-0 left-0 bg-white/30 rounded-full transition-all duration-300"
                style={{ width: `${bufferedPercent}%` }} />
              {/* Progress */}
              <div className="absolute inset-y-0 left-0 bg-brand rounded-full transition-all duration-150"
                style={{ width: `${progressPercent}%` }} />
              {/* Thumb */}
              <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow opacity-0 group-hover/bar:opacity-100 transition-opacity -translate-x-1/2"
                style={{ left: `${progressPercent}%` }} />
            </div>

            {/* Controls row */}
            <div className="flex items-center gap-2">
              {/* Play/Pause */}
              <button onClick={togglePlay} className="text-white hover:text-brand transition-colors p-1" aria-label={isPlaying ? "Pause" : "Play"}>
                {isPlaying ? <IconPause className="w-5 h-5" /> : <IconPlay className="w-5 h-5" />}
              </button>

              {/* Skip back 10s */}
              <button onClick={() => skip(-10)} className="text-white/70 hover:text-white transition-colors p-1" aria-label="Skip back 10s">
                <IconSkipBack className="w-4 h-4" />
              </button>

              {/* Skip forward 10s */}
              <button onClick={() => skip(10)} className="text-white/70 hover:text-white transition-colors p-1" aria-label="Skip forward 10s">
                <IconSkipForward className="w-4 h-4" />
              </button>

              {/* Time */}
              <span className="text-white/80 text-xs font-mono tabular-nums">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>

              <div className="flex-1" />

              {/* Volume */}
              <button onClick={toggleMute} className="text-white/70 hover:text-white transition-colors p-1" aria-label={isMuted ? "Unmute" : "Mute"}>
                {isMuted ? <IconVolumeOff className="w-4 h-4" /> : <IconVolume className="w-4 h-4" />}
              </button>

              {/* Download */}
              {downloadable && (
                <button onClick={handleDownload} disabled={downloading}
                  className="text-white/70 hover:text-white transition-colors p-1 disabled:opacity-50" aria-label="Download video">
                  {downloading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <IconDownload className="w-4 h-4" />
                  )}
                </button>
              )}

              {/* Fullscreen */}
              <button onClick={toggleFullscreen} className="text-white/70 hover:text-white transition-colors p-1" aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                {isFullscreen ? <IconMinimize className="w-4 h-4" /> : <IconMaximize className="w-4 h-4" />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
